import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import CommercialLayout from "../../components/layouts/CommercialLayout";
import api from "../../services/api";

const couleurScore = (score) => {
  if (score >= 80) return { bg: "#f0fdf4", text: "#16a34a", label: "Très fiable" };
  if (score >= 60) return { bg: "#fefce8", text: "#ca8a04", label: "Fiable" };
  if (score >= 40) return { bg: "#fff7ed", text: "#ea580c", label: "À risque" };
  return { bg: "#fef2f2", text: "#dc2626", label: "Bloqué" };
};

const StatCard = ({ icon, label, value, unit, color, bgColor, onDetail }) => (
  <div style={{ backgroundColor: "white", borderRadius: "16px", padding: "1.25rem", border: "0.5px solid #e5e7eb", display: "flex", flexDirection: "column", gap: "0.75rem" }}>
    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
      <div style={{ width: "38px", height: "38px", borderRadius: "10px", backgroundColor: bgColor, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{icon}</div>
      <span style={{ fontSize: "13px", color: "#6b7280", fontWeight: "400" }}>{label}</span>
    </div>
    <div style={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
      <span style={{ fontSize: "32px", fontWeight: "500", color: color }}>{value}</span>
      {unit && <span style={{ fontSize: "13px", color: "#6b7280" }}>{unit}</span>}
    </div>
    <button onClick={onDetail} style={{ width: "100%", padding: "0.5rem", backgroundColor: bgColor, border: "none", borderRadius: "8px", color: color, fontSize: "12px", fontWeight: "500", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
      <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
      Détails
    </button>
  </div>
);

const DashboardCommercial = () => {
  const { utilisateur } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalInfo, setModalInfo] = useState(null);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const res = await api.get(`/commercials/${utilisateur.id}/dashboard`);
        setData(res.data);
      } catch (err) {
        setError("Impossible de charger le dashboard");
      } finally {
        setLoading(false);
      }
    };
    if (utilisateur?.id) fetchDashboard();
  }, [utilisateur]);

  const today = new Date().toLocaleString("fr-FR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit", timeZoneName: "short"
  });

  if (loading) return (
    <CommercialLayout>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ width: "36px", height: "36px", border: "3px solid #E6F1FB", borderTop: "3px solid #185FA5", borderRadius: "50%", animation: "spin 1s linear infinite", margin: "0 auto 1rem" }} />
          <p style={{ color: "#6b7280", fontSize: "14px" }}>Chargement...</p>
        </div>
      </div>
    </CommercialLayout>
  );

  if (error) return (
    <CommercialLayout>
      <div style={{ padding: "2rem", textAlign: "center", color: "#dc2626" }}>{error}</div>
    </CommercialLayout>
  );

  const modals = {
    coupons: {
      titre: "Détails Coupons",
      infos: [
        { label: "Coupons vendus (total)", value: data.nombre_coupons_vendus },
        { label: "Coupons ce mois", value: data.ventes_mois?.couponsTotal || 0 },
        { label: "Moyenne par jour", value: data.ventes_mois?.moyenneCoupons || 0 },
        { label: "Jours travaillés", value: data.ventes_mois?.joursTravailles || 0 },
      ]
    },
    credits: {
      titre: "Détails Crédits",
      infos: [
        { label: "Crédits octroyés (total)", value: data.nombre_credits_octroyes },
        { label: "Remboursés ce mois", value: data.credits_rembourses_mois },
        { label: "Non remboursés", value: data.credits_non_rembourses },
        { label: "Pénalités récupérées", value: `${Number(data.penalites_recuperees || 0).toLocaleString("fr-FR")} F` },
      ]
    },
    retard: {
      titre: "Clients en retard",
      infos: [
        { label: "Clients en retard", value: data.clients_en_retard },
        { label: "Clients bloqués", value: data.clients_bloques },
        { label: "Clients à risque", value: data.clients_a_risque },
        { label: "Total clients", value: data.total_clients },
      ]
    },
    dette: {
      titre: "Détails Dette",
      infos: [
        { label: "Dette totale", value: `${Number(data.dette_totale || 0).toLocaleString("fr-FR")} F` },
        { label: "Crédits non remboursés", value: data.credits_non_rembourses },
        { label: "Pénalités en cours", value: data.clients?.filter(c => c.credit_non_rembourse?.penalites > 0).length || 0 },
      ]
    },
  };

  return (
    <CommercialLayout>
      <style>{`
        @media (max-width: 767px) {
          #solde-mobile-fixed { display: flex !important; }
          #dashboard-content { padding-top: 72px !important; }
        }
        @media (min-width: 768px) {
          #solde-mobile-fixed { display: none !important; }
          #solde-desktop { display: block !important; }
        }
      `}</style>

      {/* Solde épinglé en haut sur mobile */}
      <div id="solde-mobile-fixed" style={{
        display: "none", position: "fixed", top: "60px", left: 0, right: 0, zIndex: 50,
        background: "linear-gradient(145deg, #1d4ed8, #185FA5)",
        padding: "0.75rem 1.25rem", color: "white",
        alignItems: "center", justifyContent: "space-between",
        boxShadow: "0 2px 8px rgba(0,0,0,0.2)"
      }}>
        <div>
          <div style={{ fontSize: "11px", opacity: 0.8, marginBottom: "2px" }}>Solde</div>
          <div style={{ fontSize: "20px", fontWeight: "500" }}>
            {Number(data.solde).toLocaleString("fr-FR")} <span style={{ fontSize: "12px" }}>FCFA</span>
          </div>
        </div>
        <button onClick={() => setModalInfo("dette")} style={{ padding: "0.4rem 0.9rem", backgroundColor: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.3)", borderRadius: "8px", color: "white", fontSize: "12px", cursor: "pointer" }}>
          Détails
        </button>
      </div>

      {/* Modal détails */}
      {modalInfo && (
        <div onClick={() => setModalInfo(null)} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.4)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: "16px", padding: "1.5rem", width: "100%", maxWidth: "360px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
              <h3 style={{ fontSize: "16px", fontWeight: "500", margin: 0 }}>{modals[modalInfo]?.titre}</h3>
              <button onClick={() => setModalInfo(null)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "18px", color: "#6b7280" }}>×</button>
            </div>
            {modals[modalInfo]?.infos.map((info, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "0.65rem 0", borderBottom: i < modals[modalInfo].infos.length - 1 ? "0.5px solid #f3f4f6" : "none" }}>
                <span style={{ fontSize: "13px", color: "#6b7280" }}>{info.label}</span>
                <span style={{ fontSize: "13px", fontWeight: "500", color: "#111827" }}>{info.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Contenu principal */}
      <div id="dashboard-content">
        {/* Date */}
        <p style={{ fontSize: "12px", color: "#9ca3af", marginBottom: "1.25rem" }}>{today}</p>

        {/* Layout principal */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: "1.25rem", alignItems: "start" }}>

          {/* Colonne gauche */}
          <div>
            {/* 3 cartes stats */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "1rem", marginBottom: "1.25rem" }}>
              <StatCard
                icon={<svg width="18" height="18" fill="none" stroke="#185FA5" strokeWidth="2" viewBox="0 0 24 24"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>}
                label="Nbr Coupons vendus" value={String(data.nombre_coupons_vendus).padStart(2, "0")} unit="Coupons" color="#185FA5" bgColor="#E6F1FB" onDetail={() => setModalInfo("coupons")}
              />
              <StatCard
                icon={<svg width="18" height="18" fill="none" stroke="#0891b2" strokeWidth="2" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/><path d="M12 6v6l4 2"/></svg>}
                label="Nbr de Crédit octroyé" value={String(data.nombre_credits_octroyes).padStart(2, "0")} unit="Crédits" color="#0891b2" bgColor="#ecfeff" onDetail={() => setModalInfo("credits")}
              />
              <StatCard
                icon={<svg width="18" height="18" fill="none" stroke="#7c3aed" strokeWidth="2" viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg>}
                label="Nbr de clients en retard" value={String(data.clients_en_retard).padStart(2, "0")} unit="Clients" color="#7c3aed" bgColor="#f5f3ff" onDetail={() => setModalInfo("retard")}
              />
            </div>

            {/* Liste clients */}
            <div style={{ backgroundColor: "white", border: "0.5px solid #e5e7eb", borderRadius: "16px", padding: "1.25rem" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
                <div style={{ fontSize: "14px", fontWeight: "500", color: "#111827" }}>Mes clients</div>
                <button onClick={() => navigate("/commercial/clients")} style={{ fontSize: "12px", color: "#185FA5", background: "none", border: "none", cursor: "pointer" }}>Voir tous →</button>
              </div>
              {data.clients?.length === 0 ? (
                <p style={{ textAlign: "center", color: "#6b7280", fontSize: "14px", padding: "1rem 0" }}>Aucun client</p>
              ) : (
                data.clients?.slice(0, 5).map((client, i) => {
                  const sc = couleurScore(client.score);
                  return (
                    <div key={client.id} onClick={() => navigate(`/commercial/clients/${client.id}`)}
                      style={{ display: "flex", alignItems: "center", gap: "12px", padding: "0.7rem 0", borderBottom: i < 4 ? "0.5px solid #f3f4f6" : "none", cursor: "pointer" }}>
                      <div style={{ width: "36px", height: "36px", borderRadius: "50%", backgroundColor: sc.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", fontWeight: "500", color: sc.text, flexShrink: 0 }}>
                        {client.nom?.charAt(0)}{client.prenom?.charAt(0)}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: "13px", fontWeight: "500", color: "#111827" }}>{client.prenom} {client.nom}</div>
                        <div style={{ fontSize: "11px", color: "#6b7280" }}>Score : {client.score} · {sc.label}</div>
                      </div>
                      {client.credit_non_rembourse ? (
                        <span style={{ fontSize: "11px", backgroundColor: "#fef2f2", color: "#dc2626", padding: "2px 8px", borderRadius: "6px", flexShrink: 0 }}>Non remboursé</span>
                      ) : client.alerte_visuelle === "echeance_proche" ? (
                        <span style={{ fontSize: "11px", backgroundColor: "#fff7ed", color: "#ea580c", padding: "2px 8px", borderRadius: "6px", flexShrink: 0 }}>Échéance proche</span>
                      ) : client.alerte_visuelle === "en_retard" ? (
                        <span style={{ fontSize: "11px", backgroundColor: "#fef2f2", color: "#ea580c", padding: "2px 8px", borderRadius: "6px", flexShrink: 0 }}>En retard</span>
                      ) : client.credit_en_cours ? (
                        <span style={{ fontSize: "11px", backgroundColor: "#E6F1FB", color: "#185FA5", padding: "2px 8px", borderRadius: "6px", flexShrink: 0 }}>En cours</span>
                      ) : client.statut === "Bloque" ? (
                        <span style={{ fontSize: "11px", backgroundColor: "#fef2f2", color: "#dc2626", padding: "2px 8px", borderRadius: "6px", flexShrink: 0 }}>Bloqué</span>
                      ) : (
                        <span style={{ fontSize: "11px", backgroundColor: "#f0fdf4", color: "#16a34a", padding: "2px 8px", borderRadius: "6px", flexShrink: 0 }}>OK</span>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Colonne droite : Solde desktop */}
          <div id="solde-desktop" style={{ width: "220px", flexShrink: 0, display: "none" }}>
            <div style={{ background: "linear-gradient(145deg, #1d4ed8, #185FA5)", borderRadius: "16px", padding: "1.5rem", color: "white", marginBottom: "1rem" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "1rem" }}>
                <div style={{ width: "32px", height: "32px", borderRadius: "8px", backgroundColor: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg width="16" height="16" fill="none" stroke="white" strokeWidth="2" viewBox="0 0 24 24"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
                </div>
                <span style={{ fontSize: "13px", opacity: 0.85 }}>Solde</span>
              </div>
              <div style={{ fontSize: "28px", fontWeight: "500", marginBottom: "4px" }}>{Number(data.solde).toLocaleString("fr-FR")}</div>
              <div style={{ fontSize: "13px", opacity: 0.75, marginBottom: "1.25rem" }}>FCFA</div>
              <button onClick={() => setModalInfo("dette")} style={{ width: "100%", padding: "0.5rem", backgroundColor: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.3)", borderRadius: "8px", color: "white", fontSize: "12px", fontWeight: "500", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
                <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                Détails
              </button>
            </div>
            <div style={{ backgroundColor: "white", border: "0.5px solid #e5e7eb", borderRadius: "16px", padding: "1.25rem" }}>
              <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "6px" }}>Dette totale</div>
              <div style={{ fontSize: "22px", fontWeight: "500", color: "#dc2626", marginBottom: "0.75rem" }}>{Number(data.dette_totale || 0).toLocaleString("fr-FR")} F</div>
              <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "4px" }}>Remboursés ce mois</div>
              <div style={{ fontSize: "18px", fontWeight: "500", color: "#16a34a" }}>{data.credits_rembourses_mois}</div>
            </div>
          </div>
        </div>

        {/* Récapitulatif du jour */}
        <div style={{ backgroundColor: "white", border: "0.5px solid #e5e7eb", borderRadius: "16px", padding: "1.25rem", marginTop: "1.25rem" }}>
          <div style={{ fontSize: "14px", fontWeight: "500", color: "#111827", marginBottom: "1rem" }}>Récapitulatif du jour</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: "12px" }}>
            {[
              { label: "Clients servis", value: data.recap_jour?.nb_clients || 0, color: "#185FA5" },
              { label: "Coupons vendus", value: data.recap_jour?.nb_coupons || 0, color: "#185FA5" },
              { label: "Montant mobilisé", value: `${Number(data.recap_jour?.montant_mobilise || 0).toLocaleString("fr-FR")} F`, color: "#ea580c" },
              { label: "Montant encaissé", value: `${Number(data.recap_jour?.montant_encaisse || 0).toLocaleString("fr-FR")} F`, color: "#0f6e56" },
              { label: "Pénalités", value: `${Number(data.recap_jour?.penalites || 0).toLocaleString("fr-FR")} F`, color: Number(data.recap_jour?.penalites) > 0 ? "#dc2626" : "#9ca3af" },
            ].map((stat, i) => (
              <div key={i} style={{ padding: "0.75rem", backgroundColor: "#f9fafb", borderRadius: "10px" }}>
                <div style={{ fontSize: "11px", color: "#6b7280", marginBottom: "4px" }}>{stat.label}</div>
                <div style={{ fontSize: "16px", fontWeight: "500", color: stat.color }}>{stat.value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

    </CommercialLayout>
  );
};

export default DashboardCommercial;